import React, { useState, useEffect, useContext } from 'react'
import { IconButton, TextField, debounce, Divider } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import FileDownloadIcon from '@mui/icons-material/FileDownload'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { GridColDef, GridValueGetterParams, ptBR } from '@mui/x-data-grid'
import { Header, Content, Image, StyledDataGrid, NotaFiscalStatusIcon } from './styles'
import NotasIcon from '../../../assets/notas-icon.png'
import { isStatusSucessoNotaFiscal } from '../../../utils/Constants'
import { GFLContext } from '../../../infra/GFLContext'
import { TypeAlertEnum } from '../../../utils/enum/typeAlertEnum'
import NotaFiscalDialog, { NotaFiscalDetalhada, NotaFiscalPreenchimento } from '../../components/NotaFiscalDialog'
import NovaNotaFiscalDialog from '../../components/NovaNotaFiscalDialog'
import { EsconderPorNivelAcesso } from '../../../services/auth'
import { NivelAcessoEnum } from '../../../utils/enum/nivelAcessoEnum'
import axios from '../../../services/withAxios'

interface NotaFiscalListagem {
  id: string
  tipo: number
  data: string
  valor: number
  notaFiscalStatus: number
}

const tipoDescricao: Record<number, string> = {
  1: 'NFCE',
  2: 'ESTORNO',
  3: 'CONTINGÊNCIA',
  4: 'DEVOLUÇÃO',
  5: 'NFE',
  6: 'NFE VENDA'
}

const mapearNotaFiscalListagem = (item: any, indice: number): NotaFiscalListagem => {
  return {
    id: item['id'] != null ? String(item['id']) : `${indice}`,
    tipo: Number(item['tipoNotaFiscal'] ?? 0),
    data: String(item['dataEmissao'] ?? new Date().toISOString()),
    valor: Number(item['valorNotaFiscal'] ?? 0),
    notaFiscalStatus: Number(item['statusRetorno'] ?? 0)
  }
}

const buscarNotasFiscais = async (pesquisa: string): Promise<NotaFiscalListagem[]> => {
  const resposta = await axios.get('/notaFiscal')
  const dados = Array.isArray(resposta.data) ? resposta.data : []
  const notasFiscais = dados.map(mapearNotaFiscalListagem)

  if (!pesquisa)
    return notasFiscais

  const termo = pesquisa.toUpperCase().trim()
  return notasFiscais.filter((notaFiscal) => {
    const tipo = tipoDescricao[notaFiscal.tipo]?.toUpperCase() ?? '-'
    const data = new Date(notaFiscal.data).toLocaleString().toUpperCase()
    const valor = notaFiscal.valor.toString().toUpperCase()

    return tipo.includes(termo) || data.includes(termo) || valor.includes(termo)
  })
}

const emitirNotaFiscalDevolucao = async (
  xmlFile: File,
  infoDevolucao: NotaFiscalPreenchimento
): Promise<NotaFiscalListagem> => {
  const formData = new FormData()
  formData.append('xmlFile', xmlFile)
  formData.append('infoDevolucao', JSON.stringify(infoDevolucao))

  const resposta = await axios.post('/notaFiscal/EmitirDevolucao', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })

  const dados = resposta.data || {}

  return {
    id: dados.id != null ? String(dados.id) : `${Date.now()}`,
    tipo: dados.tipo ?? infoDevolucao.tipo,
    data: dados.data ?? infoDevolucao.data,
    valor: dados.valor ?? infoDevolucao.valor,
    notaFiscalStatus: dados.notaFiscalStatus ?? 0
  }
}

const gerarEspelhoDevolucao = async (
  xmlFile: File,
  infoDevolucao: NotaFiscalPreenchimento
): Promise<void> => {
  const formData = new FormData()
  formData.append('xmlFile', xmlFile)
  formData.append('infoDevolucao', JSON.stringify(infoDevolucao))

  const resposta = await axios.post('/notaFiscal/GerarEspelhoDevolucao', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    responseType: 'blob'
  })

  const blob = new Blob([resposta.data], { type: 'application/pdf' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'Espelho_NFe_Devolucao.pdf'
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(url)
}

const NotaFiscal: React.FC = () => {
  const [pesquisa, setPesquisa] = useState<string>('')
  const [notasFiscais, setNotasFiscais] = useState<NotaFiscalListagem[]>([])
  const [pesquisarTrigger, setPesquisarTrigger] = useState<boolean>(false)
  const [dialogAberto, setDialogAberto] = useState<boolean>(false)
  const [wizardAberto, setWizardAberto] = useState<boolean>(false)
  const [notaFiscalSelecionada, setNotaFiscalSelecionada] = useState<NotaFiscalDetalhada | null>(null)
  const [valoresIniciaisNotaFiscal, setValoresIniciaisNotaFiscal] = useState<NotaFiscalPreenchimento | null>(null)
  const [xmlDevolucaoSelecionado, setXmlDevolucaoSelecionado] = useState<File | null>(null)
  const [somenteLeitura, setSomenteLeitura] = useState<boolean>(false)

  const contexto = useContext(GFLContext)

  const pesquisar = debounce((event: React.ChangeEvent<HTMLInputElement>) => {
    setPesquisa(event.target.value)
  }, 500)

  useEffect(() => {
    const buscarDados = async () => {
      try {
        const resultado = await buscarNotasFiscais(pesquisa)
        setNotasFiscais(resultado)
      } catch (e: any) {
        contexto.setMessageAlert(`Houve um problema ao carregar as notas fiscais. Log: ${e}`)
        contexto.setTypeAlert(TypeAlertEnum.error)
        contexto.setOpenAlert(true)
      }
    }

    buscarDados()
  }, [pesquisa, pesquisarTrigger])

  /**
   * A linha da listagem NAO abre detalhe, para nenhum tipo de nota. Motivos:
   *
   * - Venda (NFC-e / NF-e de venda) e estorno: o NotaFiscalDialog e a tela de revisao de DEVOLUCAO.
   *   Abri-la para esses documentos nao faz sentido e exporia acoes de devolucao sobre o documento
   *   errado.
   * - A propria devolucao: o endpoint GET /notaFiscal/:id monta uma SUGESTAO de devolucao a partir
   *   da nota, isto e, tentaria montar uma devolucao DA devolucao. Ele foi feito para o fluxo de 3
   *   passos, que parte da nota de COMPRA do fornecedor. Como o CFOP de uma devolucao ja e o de
   *   destino (6202 / 5202) e o mapa de conversao so conhece origens (5101..6405), a resolucao de
   *   CFOP falha e a requisicao quebra para TODA devolucao ja emitida.
   *
   * A revisao da devolucao segue acessivel pelo caminho correto: botao de nova nota fiscal ->
   * upload do XML da compra -> selecao de produtos -> NotaFiscalDialog.
   * O que a linha oferece e a DANFE, no botao de acoes. Ver TECH_DEBT TD-007.
   */
  const linhaAbreDetalhe = (_notaFiscal: NotaFiscalListagem) => false

  /**
   * Baixa a DANFE da nota. Vale para todos os tipos: o backend escolhe o documento pelo modelo
   * (mod. 65 imprime o cupom com QR Code; mod. 55 imprime a DANFE retrato).
   */
  const handleBaixarDanfe = async (item: NotaFiscalListagem) => {
    try {
      const resposta = await axios({
        url: `/notaFiscal/${item.id}/GerarDanfeNFe`,
        method: 'GET',
        responseType: 'blob'
      })

      const blob = new Blob([resposta.data], { type: resposta.headers['content-type'] })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')

      link.href = url
      link.download = `danfe-${item.id}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (e: any) {
      contexto.setMessageAlert(`Houve um problema ao baixar a DANFE. Log: ${e}`)
      contexto.setTypeAlert(TypeAlertEnum.error)
      contexto.setOpenAlert(true)
    }
  }

  const getNotaFiscalStatusIcon = (status: number) => {
    let title = ''
    let backgroundColor = ''

    if (isStatusSucessoNotaFiscal(status)) {
      title = 'Nota Fiscal emitida com sucesso'
      backgroundColor = '#4caf50'
    } else if (status > 0 && status !== 100) {
      title = 'Erro na emissão da Nota Fiscal'
      backgroundColor = '#f44336'
    } else {
      title = 'Nota Fiscal pendente'
      backgroundColor = '#9e9e9e'
    }

    return <NotaFiscalStatusIcon backgroundColor={backgroundColor} title={title} />
  }

  const colunas: GridColDef[] = [
    {
      field: 'tipo',
      headerName: 'Tipo',
      flex: 1,
      valueGetter: (getter: GridValueGetterParams) => {
        return tipoDescricao[getter.value as number] ?? '-'
      }
    },
    {
      field: 'data',
      headerName: 'Data',
      flex: 1,
      valueGetter: (data: GridValueGetterParams<Date, any>) => {
        return new Date(data.value).toLocaleString().slice(0, -3)
      }
    },
    {
      field: 'valor',
      headerName: 'Valor',
      flex: 1,
      valueFormatter: (params: any) => {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(params.value)
      }
    },
    {
      field: 'notaFiscalStatus',
      headerName: 'Status',
      width: 100,
      align: 'center',
      renderCell: (cellValues) => {
        return getNotaFiscalStatusIcon(cellValues.value as number)
      },
      sortable: false
    },
    {
      field: 'acoes',
      headerName: 'Ações',
      width: 140,
      align: 'center',
      renderCell: (cellValues) => {
        const notaFiscal = cellValues.row as NotaFiscalListagem
        // Sem autorizacao (status 100) nao existe DANFE para imprimir.
        const danfeDesabilitada = notaFiscal.notaFiscalStatus !== 100

        return (
          <IconButton
            color="success"
            title={danfeDesabilitada ? 'Disponível apenas para notas autorizadas' : 'Baixar DANFE (PDF)'}
            onClick={(event) => {
              event.stopPropagation()
              handleBaixarDanfe(notaFiscal)
            }}
            component="span"
            disabled={danfeDesabilitada}
          >
            <FileDownloadIcon />
          </IconButton>
        )
      },
      sortable: false
    }
  ]

  const handleAdicionarNotaFiscal = async (notaFiscalNova: NotaFiscalPreenchimento) => {
    if (!xmlDevolucaoSelecionado) {
      contexto.setMessageAlert('XML da nota fiscal de origem não encontrado. Inicie o fluxo novamente.')
      contexto.setTypeAlert(TypeAlertEnum.error)
      contexto.setOpenAlert(true)
      return
    }

    try {
      await emitirNotaFiscalDevolucao(xmlDevolucaoSelecionado, notaFiscalNova)
      setValoresIniciaisNotaFiscal(null)
      setXmlDevolucaoSelecionado(null)
      setPesquisarTrigger((valorAtual) => !valorAtual)
    } catch (e: any) {
      contexto.setMessageAlert(`Houve um problema ao criar a nota fiscal. Log: ${e}`)
      contexto.setTypeAlert(TypeAlertEnum.error)
      contexto.setOpenAlert(true)
    }
  }

  const handleGerarEspelho = async (notaFiscalNova: NotaFiscalPreenchimento) => {
    if (!xmlDevolucaoSelecionado) {
      contexto.setMessageAlert('XML da nota fiscal de origem não encontrado. Inicie o fluxo novamente.')
      contexto.setTypeAlert(TypeAlertEnum.error)
      contexto.setOpenAlert(true)
      return
    }

    try {
      await gerarEspelhoDevolucao(xmlDevolucaoSelecionado, notaFiscalNova)
    } catch (e: any) {
      contexto.setMessageAlert(`Houve um problema ao gerar o espelho da nota fiscal. Log: ${e}`)
      contexto.setTypeAlert(TypeAlertEnum.error)
      contexto.setOpenAlert(true)
    }
  }

  const handleAtualizarNotaFiscal = (_notaFiscalAtualizada: NotaFiscalDetalhada) => {
    setNotaFiscalSelecionada(null)
    setPesquisarTrigger((valorAtual) => !valorAtual)
  }

  const handleAbrirNovaNotaFiscal = () => {
    setSomenteLeitura(false)
    setNotaFiscalSelecionada(null)
    setValoresIniciaisNotaFiscal(null)
    setXmlDevolucaoSelecionado(null)
    setWizardAberto(true)
  }

  const handleConfirmarWizardNotaFiscal = (preenchimento: NotaFiscalPreenchimento, xmlFile: File) => {
    setSomenteLeitura(false)
    setNotaFiscalSelecionada(null)
    setValoresIniciaisNotaFiscal(preenchimento)
    setXmlDevolucaoSelecionado(xmlFile)
    setDialogAberto(true)
  }

  return (
    <>
      <Image src={NotasIcon} />
      <Header>
        <TextField
          id="pesquisa"
          label="Pesquisar"
          onChange={pesquisar}
          type="search"
          fullWidth
          autoFocus
          autoComplete='off'
          inputProps={{ style: { textTransform: "uppercase" } }}
        />
        <IconButton
          color="primary"
          onClick={_ => setPesquisarTrigger(!pesquisarTrigger)}
        >
          <SearchIcon />
        </IconButton>
        <EsconderPorNivelAcesso nivelAcesso={[NivelAcessoEnum.admin, NivelAcessoEnum.gerente, NivelAcessoEnum.usuario]}>
          <>
            <Divider orientation="vertical" variant="middle" flexItem />
            <IconButton
              onClick={handleAbrirNovaNotaFiscal}
              color="primary"
            >
              <AddCircleIcon />
            </IconButton>
          </>
        </EsconderPorNivelAcesso>
      </Header>
      <Content>
        <StyledDataGrid
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          columns={colunas}
          rows={notasFiscais}
          disableColumnMenu={true}
          hideFooterSelectedRowCount={true}
          getRowClassName={(params) =>
            linhaAbreDetalhe(params.row as NotaFiscalListagem) ? 'linha-clicavel' : 'linha-sem-detalhe'
          }
          sx={{
            textTransform: 'uppercase',
            '& .linha-sem-detalhe': { cursor: 'default' },
            '& .linha-clicavel': { cursor: 'pointer' }
          }}
        />
      </Content>
      {dialogAberto ? (
        <NotaFiscalDialog
          aberto={dialogAberto}
          setAberto={setDialogAberto}
          onAdicionar={handleAdicionarNotaFiscal}
          entidade={notaFiscalSelecionada}
          onAtualizar={handleAtualizarNotaFiscal}
          somenteLeitura={somenteLeitura}
          valoresIniciais={valoresIniciaisNotaFiscal}
          onGerarEspelho={handleGerarEspelho}
        />
      ) : <></>}
      {wizardAberto ? (
        <NovaNotaFiscalDialog
          aberto={wizardAberto}
          setAberto={setWizardAberto}
          onConfirmar={handleConfirmarWizardNotaFiscal}
        />
      ) : <></>}
    </>
  )
}

export default NotaFiscal
